import { KeyRound, Pencil, Plus, ShieldCheck, UserCheck, UserX } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ApiError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { ErrorState, LoadingState } from "../components/PageState";
import { matchesQuickSearch, QuickSearch } from "../components/QuickSearch";
import { Message, Modal } from "../components/Ui";
import type { PermissionOption, RoleOption, UserRow } from "../types";

type UserForm = {
  username: string;
  displayName: string;
  email: string;
  password: string;
  roles: string[];
  permissions: string[];
};

const blankForm = (): UserForm => ({
  username: "",
  displayName: "",
  email: "",
  password: "",
  roles: [],
  permissions: []
});

const moduleLabels: Record<string, string> = {
  Dashboard: "Tổng quan", MasterData: "Danh mục", Sales: "Kinh doanh", Purchasing: "Mua hàng",
  Planning: "Kế hoạch sản xuất", Production: "Báo cáo sản xuất", Warehouse: "Kho",
  Quality: "Chất lượng (QC/QA)", IsoQuality: "Hồ sơ ISO / QA", Equipment: "Thiết bị",
  HumanResources: "Nhân sự", Accounting: "Kế toán", Traceability: "Truy xuất",
  Reports: "Báo cáo", System: "Phân quyền"
};
const actionLabels: Record<string, string> = {
  View: "Xem", Create: "Lập", Edit: "Sửa", Complete: "Hoàn tất / Duyệt", Approve: "Phê duyệt",
  Cancel: "Hủy", Adjust: "Điều chỉnh", Export: "Xuất file"
};
const actionOrder = ["View", "Create", "Edit", "Complete", "Approve", "Cancel", "Adjust", "Export"];
const moduleOrder = [
  "Dashboard", "MasterData", "Sales", "Purchasing", "Planning", "Production", "Warehouse",
  "Quality", "IsoQuality", "Equipment", "HumanResources", "Accounting", "Traceability", "Reports", "System"
];

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [permissions, setPermissions] = useState<PermissionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState<UserForm>(blankForm());
  const [passwordTarget, setPasswordTarget] = useState<UserRow | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [search, setSearch] = useState("");

  const isAdmin = currentUser?.roles.includes("ADMIN");
  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError("");
    try {
      const [userData, roleData, permissionData] = await Promise.all([
        apiRequest<UserRow[]>("/api/users"),
        apiRequest<RoleOption[]>("/api/users/roles"),
        apiRequest<PermissionOption[]>("/api/users/permissions")
      ]);
      setUsers(userData);
      setRoles(roleData);
      setPermissions(permissionData);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được người dùng và vai trò.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { void load(); }, [load]);

  const filteredUsers = users.filter((row) => matchesQuickSearch(
    search,
    row.username,
    row.displayName,
    row.email,
    row.isActive ? "hoạt động" : "đã khóa",
    row.roles.map((role) => `${role.code} ${role.name}`).join(" ")
  ));

  const openCreate = () => {
    setEditingId("");
    setForm(blankForm());
    setError("");
    setShowForm(true);
  };

  const openEdit = (row: UserRow) => {
    setEditingId(row.id);
    setForm({
      username: row.username,
      displayName: row.displayName,
      email: row.email ?? "",
      password: "",
      roles: row.roles.map((role) => role.code),
      permissions: row.permissions
    });
    setError("");
    setShowForm(true);
  };

  const toggleRole = (code: string) => {
    setForm((current) => {
      const nextRoles = current.roles.includes(code)
        ? current.roles.filter((role) => role !== code)
        : [...current.roles, code];
      const templatePermissions = nextRoles.includes("ADMIN")
        ? permissions.map((permission) => permission.code)
        : Array.from(new Set(roles.filter((role) => nextRoles.includes(role.code)).flatMap((role) => role.permissions)));
      return { ...current, roles: nextRoles, permissions: templatePermissions };
    });
  };

  const togglePermission = (permission: PermissionOption) => {
    if (form.roles.includes("ADMIN")) return;
    setForm((current) => {
      const selected = new Set(current.permissions);
      if (selected.has(permission.code)) {
        selected.delete(permission.code);
        if (permission.action === "View")
          permissions.filter((x) => x.module === permission.module).forEach((x) => selected.delete(x.code));
      } else {
        selected.add(permission.code);
        const viewPermission = permissions.find((x) => x.module === permission.module && x.action === "View");
        if (viewPermission) selected.add(viewPermission.code);
      }
      return { ...current, permissions: Array.from(selected) };
    });
  };

  const save = async () => {
    setError("");
    setMessage("");
    if (!form.displayName.trim() || !form.roles.length || !form.permissions.length) {
      setError("Họ tên, ít nhất một vai trò và ít nhất một quyền xem là bắt buộc.");
      return;
    }
    if (!editingId && (form.username.trim().length < 3 || form.password.length < 8)) {
      setError("Tên đăng nhập tối thiểu 3 ký tự và mật khẩu tối thiểu 8 ký tự.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiRequest<void>(`/api/users/${editingId}`, {
          method: "PUT",
          body: JSON.stringify({ displayName: form.displayName, email: form.email || null, roles: form.roles, permissions: form.permissions })
        });
        setMessage("Đã cập nhật người dùng và vai trò. Quyền mới có hiệu lực khi tài khoản đăng nhập lại.");
      } else {
        await apiRequest("/api/users", {
          method: "POST",
          body: JSON.stringify({
            username: form.username,
            displayName: form.displayName,
            email: form.email || null,
            password: form.password,
            roles: form.roles,
            permissions: form.permissions
          })
        });
        setMessage("Đã tạo người dùng. Người dùng phải đổi mật khẩu trong lần đăng nhập đầu tiên.");
      }
      setShowForm(false);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được người dùng.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: UserRow) => {
    try {
      await apiRequest<void>(`/api/users/${row.id}/active`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !row.isActive })
      });
      setMessage(row.isActive ? "Đã khóa tài khoản." : "Đã mở khóa tài khoản.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không cập nhật được người dùng.");
    }
  };

  const resetPassword = async () => {
    if (!passwordTarget) return;
    setError("");
    if (newPassword.length < 8) {
      setError("Mật khẩu mới phải có tối thiểu 8 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Hai lần nhập mật khẩu chưa giống nhau.");
      return;
    }
    setSaving(true);
    try {
      await apiRequest<void>(`/api/users/${passwordTarget.id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ newPassword })
      });
      setPasswordTarget(null);
      setNewPassword("");
      setConfirmPassword("");
      setMessage("Đã đặt lại mật khẩu. Tài khoản phải đổi mật khẩu sau khi đăng nhập.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không đặt lại được mật khẩu.");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <section className="panel access-denied">
        <ShieldCheck size={40} />
        <h2>Chức năng dành cho Quản trị hệ thống</h2>
        <p>Tài khoản hiện tại không có quyền quản lý người dùng.</p>
      </section>
    );
  }
  if (loading && !users.length) return <LoadingState label="Đang tải người dùng và vai trò..." />;
  if (error && !users.length && !showForm) return <ErrorState message={error} retry={load} />;

  return (
    <section className="panel full-panel">
      <div className="list-toolbar">
        <div>
          <span className="eyebrow">QUẢN TRỊ HỆ THỐNG</span>
          <h2>Người dùng và phân quyền</h2>
          <p>Tạo tài khoản, gán vai trò, khóa tài khoản và đặt lại mật khẩu.</p>
        </div>
        <div className="user-toolbar-actions">
          <span className="status-pill info">{users.length} tài khoản</span>
          <button className="primary-button" onClick={openCreate}><Plus size={17} /> Thêm người dùng</button>
        </div>
      </div>
      {message && <Message tone="success">{message}</Message>}
      {error && <Message tone="error">{error}</Message>}
      <QuickSearch value={search} onChange={setSearch} placeholder="Tìm tên đăng nhập, họ tên, email, vai trò hoặc trạng thái..." resultCount={filteredUsers.length} />
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th className="stt-column">STT</th><th>Tài khoản</th><th>Người dùng</th><th>Vai trò</th>
              <th>Lần đăng nhập cuối</th><th>Trạng thái</th><th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((row, index) => (
              <tr key={row.id}>
                <td className="stt-column">{index + 1}</td>
                <td><strong className="code-cell">{row.username}</strong>{row.mustChangePassword && <small className="table-subtext">Phải đổi mật khẩu</small>}</td>
                <td><strong>{row.displayName}</strong><small className="table-subtext">{row.email ?? "—"}</small></td>
                <td><div className="role-list">{row.roles.map((role) => <span key={role.code}>{role.name}</span>)}</div><small className="table-subtext">{row.useCustomPermissions ? `${row.permissions.length} quyền được cấu hình riêng` : "Theo quyền mặc định của vai trò"}</small></td>
                <td>{row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString("vi-VN") : "Chưa đăng nhập"}</td>
                <td><span className={`status-dot ${row.isActive ? "online" : ""}`}>{row.isActive ? "Hoạt động" : "Đã khóa"}</span></td>
                <td>
                  <div className="table-actions user-actions">
                    <button onClick={() => openEdit(row)} title="Sửa người dùng và vai trò"><Pencil size={17} /></button>
                    <button onClick={() => { setPasswordTarget(row); setNewPassword(""); setConfirmPassword(""); setError(""); }} title="Đặt lại mật khẩu"><KeyRound size={17} /></button>
                    <button
                      onClick={() => void toggleActive(row)}
                      disabled={row.id === currentUser?.id}
                      title={row.id === currentUser?.id ? "Không thể tự khóa tài khoản" : row.isActive ? "Khóa tài khoản" : "Mở khóa tài khoản"}
                    >
                      {row.isActive ? <UserX size={17} /> : <UserCheck size={17} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!filteredUsers.length && <tr><td colSpan={7}><div className="empty-state">Không có người dùng phù hợp bộ lọc.</div></td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && <Modal title={editingId ? "Sửa người dùng" : "Thêm người dùng"} subtitle="Chọn vai trò mẫu, sau đó điều chỉnh quyền riêng cho người dùng." onClose={() => setShowForm(false)} wide error={error}>
        <div className="form-grid three">
          <label>Tên đăng nhập *<input disabled={!!editingId} value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} autoComplete="off" /></label>
          <label>Họ và tên *<input value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} /></label>
          <label>Email<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
          {!editingId && <label>Mật khẩu ban đầu *<input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} autoComplete="new-password" /></label>}
        </div>
        <section className="role-selector">
          <div className="line-editor-header"><strong>Vai trò được cấp *</strong><span>Chọn ít nhất một vai trò</span></div>
          <div className="role-option-grid">
            {roles.map((role) => <label className={`role-option ${form.roles.includes(role.code) ? "selected" : ""}`} key={role.code}>
              <input type="checkbox" checked={form.roles.includes(role.code)} onChange={() => toggleRole(role.code)} />
              <span><strong>{role.name}</strong><small>{role.description ?? role.code}</small></span>
            </label>)}
          </div>
        </section>
        <section className="permission-selector">
          <div className="line-editor-header">
            <div><strong>Quyền chi tiết theo mô-đun *</strong><small className="table-subtext">Bỏ chọn các thao tác không được phép; quyền Xem là điều kiện để sử dụng mô-đun.</small></div>
            <span>{form.roles.includes("ADMIN") ? "Quản trị có toàn quyền" : `${form.permissions.length} quyền đã chọn`}</span>
          </div>
          <div className="data-table-wrapper permission-matrix-wrapper">
            <table className="data-table permission-matrix">
              <thead><tr><th>Mô-đun</th>{actionOrder.map((action) => <th className="center-cell" key={action}>{actionLabels[action]}</th>)}</tr></thead>
              <tbody>{moduleOrder.map((module) => <tr key={module}>
                <td><strong>{moduleLabels[module]}</strong></td>
                {actionOrder.map((action) => {
                  const permission = permissions.find((item) => item.module === module && item.action === action);
                  return <td className="center-cell" key={`${module}.${action}`}>{permission ? <input
                    type="checkbox"
                    aria-label={`${moduleLabels[module]} – ${actionLabels[action]}`}
                    checked={form.roles.includes("ADMIN") || form.permissions.includes(permission.code)}
                    disabled={form.roles.includes("ADMIN")}
                    onChange={() => togglePermission(permission)}
                  /> : "—"}</td>;
                })}
              </tr>)}</tbody>
            </table>
          </div>
        </section>
        <div className="form-actions"><button className="secondary-button" onClick={() => setShowForm(false)}>Đóng</button><button className="primary-button" disabled={saving} onClick={() => void save()}>{saving ? "Đang lưu..." : "Lưu người dùng"}</button></div>
      </Modal>}

      {passwordTarget && <Modal title="Đặt lại mật khẩu" subtitle={`${passwordTarget.displayName} – ${passwordTarget.username}`} onClose={() => setPasswordTarget(null)} error={error}>
        <div className="form-grid">
          <label>Mật khẩu mới *<input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" /></label>
          <label>Nhập lại mật khẩu *<input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" /></label>
        </div>
        <p className="form-help">Tối thiểu 8 ký tự. Người dùng sẽ phải đổi mật khẩu sau khi đăng nhập.</p>
        <div className="form-actions"><button className="secondary-button" onClick={() => setPasswordTarget(null)}>Đóng</button><button className="primary-button" disabled={saving} onClick={() => void resetPassword()}>{saving ? "Đang lưu..." : "Đặt lại mật khẩu"}</button></div>
      </Modal>}
    </section>
  );
}
